import React, { useRef, useState, useEffect } from 'react';
import { Clock, ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Define the structure of a Product
export interface Product {
  id: number;
  name: string;
  image: string;
  price: string;
  originalPrice?: string; 
  discount?: string;
  category: string;
  dateViewed: string;
}

const ActivityHistory: React.FC = () => {
  const navigate = useNavigate();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  
  // State for dynamic history
  const [history, setHistory] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  // --- FETCH DATA LOGIC ---
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const userStr = localStorage.getItem('currentUser');
        const user = userStr ? JSON.parse(userStr) : null;

        if (!user || !user.id) {
             setLoading(false);
             return;
        }
        
        const buyerId = user.id; 
        const response = await fetch(`http://localhost:2026/api/history/${buyerId}`);
        
        if (response.ok) {
            const data = await response.json();
            if (Array.isArray(data)) {
                setHistory(data);
            }
        }
      } catch (error) {
        console.error("Error fetching activity history:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, []);

  // --- SCROLL LOGIC ---
  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollAmount = container.clientWidth / 1.5; 
      
      const newScrollLeft = direction === 'left' 
        ? container.scrollLeft - scrollAmount 
        : container.scrollLeft + scrollAmount;

      container.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 10);
      setShowRightArrow(Math.ceil(scrollLeft + clientWidth) < scrollWidth - 10);
    }
  };

  const clearHistory = () => {
      // FIX: Added 'window.' to fix the "Unexpected use of 'confirm'" error
      if(window.confirm("Are you sure you want to clear your browsing history?")) {
          setHistory([]);
          // You can also add an API call here to clear backend history
      }
  }

  // Navigate to product page
  const handleViewDetails = (productId: number) => {
      navigate(`/product/${productId}`);
  };

  if (loading || history.length === 0) return null;

  return (
    <div className="w-full bg-white py-6 md:py-8 border-t border-gray-100 mt-8">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Clock className="w-6 h-6 text-teal-700" />
            Your Browsing History
          </h2>

          {/* Button is commented out as per your file, uncomment if needed */}
          {/* <button 
            onClick={clearHistory}
            className="text-red-500 hover:text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-full transition-all text-sm font-medium"
          >
            Clear History
          </button> */}
        </div>

        {/* Carousel Container */}
        <div className="relative group/carousel">
          
          {/* Left Arrow */}
          <button 
            onClick={() => scroll('left')}
            className={`absolute left-0 top-[45%] -translate-y-1/2 -ml-2 z-20 w-10 h-10 bg-white border border-gray-200 text-gray-700 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:bg-teal-600 hover:text-white hover:border-teal-600 ${showLeftArrow ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4 pointer-events-none'}`}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          {/* Right Arrow */}
          <button 
            onClick={() => scroll('right')}
            className={`absolute right-0 top-[45%] -translate-y-1/2 -mr-2 z-20 w-10 h-10 bg-white border border-gray-200 text-gray-700 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:bg-teal-600 hover:text-white hover:border-teal-600 ${showRightArrow ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4 pointer-events-none'}`}
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Scrollable Area */}
          <div 
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="flex overflow-x-auto gap-4 pb-4 scrollbar-hide snap-x"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {history.map((item) => (
              <div 
                key={item.id} 
                onClick={() => handleViewDetails(item.id)}
                // FIX: Changed from 'min-w' to 'w' and added 'flex-none'. 
                // This ensures cards stay the exact same size regardless of how many items there are.
                className="w-[45%] md:w-[30%] lg:w-[15.5%] flex-none snap-start 
                           bg-blue-100 rounded-xl p-3 hover:shadow-md 
                           transition-shadow duration-300 flex flex-col items-center text-center group/card cursor-pointer"
              >
                
                {/* Image Section */}
                <div className="w-full h-32 mb-3 flex items-center justify-center bg-white rounded-lg p-2 overflow-hidden relative">
                   {/* Discount Badge */}
                   {item.discount && (
                     <span className="absolute top-2 left-2 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-sm z-10">
                       {item.discount}
                     </span>
                   )}
                   
                   <img 
                    src={item.image} 
                    alt={item.name} 
                    className="max-h-full max-w-full object-contain group-hover/card:scale-110 transition-transform duration-300"
                  />
                </div>

                {/* Content Section */}
                <div className="w-full flex flex-col flex-grow">
                  
                  <span className="text-xs text-gray-500 mb-1">
                    {item.category}
                  </span>

                  <h3 className="text-sm font-semibold text-gray-800 line-clamp-1 mb-1" title={item.name}>
                    {item.name}
                  </h3>

                  <p className="text-teal-700 font-bold text-base mb-3">{item.price}</p>

                  <button 
                      onClick={(e) => {
                          e.stopPropagation(); // Prevents double click
                          handleViewDetails(item.id);
                      }}
                      className="mt-auto w-full py-1.5 text-xs font-semibold text-red-700 border bg-white border-red-200 rounded-full hover:bg-red-600 hover:text-white transition-colors flex items-center justify-center gap-1"
                  >
                    <Eye className="w-3 h-3" />
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        
      </div>
    </div>
  );
};

export default ActivityHistory;